<template>
  <div class="users-container">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          Users
        </h1>
      </div>

      <div class="main-content">
        <div class="table-area table-area--pad">
          <div v-if="loading" class="d-flex justify-center py-12">
            <v-progress-circular indeterminate color="primary"/>
          </div>

          <div v-else-if="!users.length" class="empty-state text-center">
            <div class="empty-state-icon mb-4">
              <v-icon size="80" color="grey-lighten-2">
                mdi-account-group-outline
              </v-icon>
            </div>
            <div class="empty-state-text mb-4">
              <h3 class="ds-panel-title mb-1">
                No Users Found
              </h3>
              <p class="text-body-3 text--secondary">
                No users are registered yet.
              </p>
            </div>
          </div>

          <v-data-table
            v-else
            :headers="headers"
            :items="users"
            :loading="loading"
            class="mank-data-table"
            :items-per-page="-1"
            hide-default-footer
            no-data-text="No users found"
            loading-text="Loading...">
            <template v-slot:item.id="{ item }">
              <div class="d-flex align-center">
                <v-avatar size="32" class="user-avatar mr-3">
                  <span class="text-element-3 font-weight-medium">{{ getInitials(item) }}</span>
                </v-avatar>
                <span class="text-element-2 font-weight-medium">{{ getFullName(item) }}</span>
              </div>
            </template>
            <template v-slot:item.email="{ item }">
              <span class="text-element-2 text--secondary">{{ item.email }}</span>
            </template>
            <template v-slot:item.roles="{ item }">
              <div class="d-flex flex-wrap gap-1">
                <v-chip
                  v-for="role in (item.roles || [])"
                  :key="role.id || role"
                  x-small
                  outlined
                  class="role-chip">
                  {{ typeof role === 'string' ? role : (role.name || '—') }}
                </v-chip>
                <span v-if="!(item.roles || []).length" class="text--secondary text-element-3">—</span>
              </div>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn v-if="hasPermission('write:user')" icon small @click="openEditDialog(item)" :disabled="item.email === 'admin@mank.com'">
                <v-icon size="18">
                  mdi-account-edit-outline
                </v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </div>
      </div>
    </div>

    <!-- Edit User Dialog -->
    <v-dialog v-model="editDialog" max-width="520" persistent>
      <v-card class="mank-dialog">
        <div class="dialog-title">
          Edit User
        </div>
        <div class="dialog-body">
          <div v-if="rolesLoading" class="d-flex justify-center py-6">
            <v-progress-circular indeterminate color="primary"/>
          </div>
          <div v-else>
            <div class="form-field">
              <label class="field-label">First Name</label>
              <MankTextField
                v-model="form.given_name"
                hide-details="auto"/>
            </div>
            <div class="form-field">
              <label class="field-label">Last Name</label>
              <MankTextField
                v-model="form.family_name"
                hide-details="auto"/>
            </div>
            <div class="form-field">
              <label class="field-label">Roles</label>
              <div class="roles-list">
                <v-checkbox
                  v-for="role in allRoles"
                  :key="role.id"
                  v-model="form.roles"
                  :value="role.id"
                  :label="role.name"
                  hide-details
                  class="mb-2"/>
              </div>
            </div>
          </div>
        </div>
        <div class="dialog-actions">
          <v-btn text class="mank-btn mank-btn--text" @click="editDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            class="mank-btn"
            elevation="0"
            :loading="saving"
            @click="saveUser">
            Save
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import UserService from '../services/UserService';
import RoleService from '../services/RoleService';
import MankTextField from '../components/MankTextField.vue';

export default {
  name: 'Users',
  components: { MankTextField },
  data() {
    return {
      users: [],
      allRoles: [],
      loading: false,
      rolesLoading: false,
      saving: false,
      editDialog: false,
      selectedUser: null,
      form: { given_name: '', family_name: '', roles: [] },
      headers: [
        { text: 'NAME', value: 'id', sortable: false },
        { text: 'EMAIL', value: 'email', sortable: true },
        { text: 'ROLES', value: 'roles', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
    };
  },
  computed: { ...mapGetters('auth', ['hasPermission']) },
  mounted() {
    this.fetchUsers();
  },
  methods: {
    async fetchUsers() {
      if (!this.$store.getters['auth/isAuthenticated']) return;
      this.loading = true;
      try {
        const result = await UserService.getAll();
        this.users = Array.isArray(result) ? result : (result?.data || []);
      } catch (e) {
        this.$store.dispatch('ui/showNotification', { type: 'error', message: 'Failed to load users.' });
      } finally {
        this.loading = false;
      }
    },
    getFullName(user) {
      const first = user.given_name || '';
      const last = user.family_name || '';
      return (first + ' ' + last).trim() || user.email || '—';
    },
    getInitials(user) {
      const name = this.getFullName(user);
      const parts = name.split(' ').filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return parts[0] ? parts[0][0].toUpperCase() : 'U';
    },
    async openEditDialog(user) {
      this.selectedUser = user;
      this.form = {
        given_name: user.given_name || '',
        family_name: user.family_name || '',
        roles: (user.roles || []).map((r) => r.id || r),
      };
      this.editDialog = true;
      this.rolesLoading = true;
      try {
        this.allRoles = await RoleService.getAll();
      } catch (e) {
        this.$store.dispatch('ui/showNotification', { type: 'error', message: 'Failed to load roles.' });
      } finally {
        this.rolesLoading = false;
      }
    },
    async saveUser() {
      this.saving = true;
      try {
        await UserService.updateUser(this.selectedUser.id, this.form);
        this.$store.dispatch('ui/showNotification', { type: 'success', message: 'User updated.' });
        this.editDialog = false;
        this.fetchUsers();
      } catch (e) {
        this.$store.dispatch('ui/showNotification', { type: 'error', message: 'Failed to update user.' });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.users-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content-area {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 32px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: $light-foreground-1;
}

.main-content {
  flex: 1;
}

.table-area {
  background: $light-surface-1;
  border-radius: 12px;
  border: 1px solid $light-border;

  &--pad {
    padding: 24px;
  }
}

.empty-state {
  padding: 60px 0;
}

.user-avatar {
  background-color: $light-surface-2 !important;
  color: $light-primary-neutral !important;
  border: 1px solid $light-border !important;
  flex-shrink: 0;
}

.dialog-title {
  font-size: 18px;
  font-weight: 700;
  padding: 24px 24px 16px;
}

.dialog-body {
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  color: $light-foreground-1;
  font-weight: 600;
  font-size: $body-3-size;
  line-height: 1.2;
}

.dialog-actions {
  padding: 16px 24px 24px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.roles-list {
  max-height: 240px;
  overflow-y: auto;
}

.role-chip {
  font-size: 11px;
}
</style>
